(() => {
  const validSellingPrice = item => item?.price_type === 'selling'
    && ['USD', 'PHP'].includes(item.price_currency)
    && typeof item.price_amount === 'number' && Number.isFinite(item.price_amount)
    && item.price_amount > 0 && Number.isSafeInteger(Math.round(item.price_amount * 100))
    && Math.abs(item.price_amount * 100 - Math.round(item.price_amount * 100)) < 1e-6;
  const formatSellingPrice = item => window.EasternNativeCatalog?.formatPrice(item) || `${item.price_currency} ${item.price_amount.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const normalizeSearchText = value => String(value || '').normalize('NFKD').replace(/\p{M}/gu, '').toLocaleLowerCase();
  const searchTokens = value => normalizeSearchText(value).match(/[a-z0-9]+|[\p{Script=Han}]+/gu) || [];
  // The index is a theme asset, not HTML. Keep a damaged or wrongly edited
  // index from creating executable links or loading images from other sites.
  function readSearchIndex(value) {
    if (!Array.isArray(value)) throw new Error('Invalid search index');
    return value.slice(0, 2000).flatMap(page => {
      if (!page || typeof page !== 'object' || typeof page.title !== 'string'
        || typeof page.url !== 'string' || !/^\/(?:$|(?:pages|blogs|policies)\/)/.test(page.url)
        || /[\u0000-\u0020\\]/.test(page.url)) return [];
      const url = new URL(page.url, location.origin);
      if (url.origin !== location.origin) return [];
      const entry = {
        title: page.title.slice(0, 300), url: url.pathname + url.search + url.hash,
        description: typeof page.description === 'string' ? page.description.slice(0, 2000) : '',
        text: typeof page.text === 'string' ? page.text.slice(0, 30000) : '',
        text_zh: typeof page.text_zh === 'string' ? page.text_zh.slice(0, 30000) : ''
      };
      if (typeof page.piece_id === 'string' && /^[a-z0-9][a-z0-9-]{0,95}$/.test(page.piece_id)
        && typeof page.image === 'string' && /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,160}\.(?:webp|png|jpe?g|avif)$/.test(page.image)) {
        entry.piece_id = page.piece_id; entry.image = page.image;
        entry.image_alt = typeof page.image_alt === 'string' ? page.image_alt.slice(0, 500) : entry.title;
        if (validSellingPrice(page)) {
          entry.price_amount = page.price_amount;
          entry.price_currency = page.price_currency;
          entry.price_type = 'selling';
        }
      }
      return [entry];
    });
  }
  const containsSearchTerm = (words, term) => words.some(word => /\p{Script=Han}/u.test(term) ? word.includes(term) : word.startsWith(term));
  function scoreSearchPage(page, terms) {
    const title = searchTokens(page.title + ' ' + (window.EasternI18n?.zh(page.title) || ''));
    const description = searchTokens(page.description + ' ' + (window.EasternI18n?.zh(page.description) || ''));
    const words = searchTokens(`${page.title} ${page.description} ${page.text} ${window.EasternI18n?.zh(page.title) || ''} ${window.EasternI18n?.zh(page.description) || ''} ${page.text_zh || ''}`);
    return {
      ...page,
      score: terms.reduce((score, term) => score + (containsSearchTerm(title, term) ? 4 : containsSearchTerm(description, term) ? 2 : containsSearchTerm(words, term) ? 1 : 0), 0),
      matches: terms.every(term => containsSearchTerm(words, term))
    };
  }
  function oneSearchEditApart(a, b) {
    if (a === b || Math.abs(a.length - b.length) > 1) return false;
    if (a.length === b.length) {
      const differences = [...a].map((letter, index) => letter !== b[index] ? index : -1).filter(index => index >= 0);
      if (differences.length === 1) return true;
      return differences.length === 2 && differences[1] === differences[0] + 1
        && a[differences[0]] === b[differences[1]] && a[differences[1]] === b[differences[0]];
    }
    const shorter = a.length < b.length ? a : b, longer = a.length < b.length ? b : a;
    let index = 0;
    while (index < shorter.length && shorter[index] === longer[index]) index += 1;
    return shorter.slice(index) === longer.slice(index + 1);
  }
  function suggestSearchQuery(terms, pages) {
    if (!terms.length || terms.length > 4) return '';
    const dictionary = ['ring','rings','earring','earrings','necklace','necklaces','pendant','pendants','bracelet','bracelets','brooch','brooches','accessories','blue','green','violet','pink','red','multicolor','yellow','white','payment','delivery','returns','aftercare'];
    const availableWords = pages.flatMap(page => searchTokens(`${page.title} ${page.description} ${page.text}`));
    let changes = 0;
    const suggestion = terms.map(term => {
      if (term.length < 4 || containsSearchTerm(availableWords, term)) return term;
      const candidates = dictionary.filter(word => word[0] === term[0] && oneSearchEditApart(term, word));
      if (candidates.length !== 1) return term;
      changes += 1;
      return candidates[0];
    });
    return changes === 1 && pages.some(page => scoreSearchPage(page, suggestion).matches) ? suggestion.join(' ') : '';
  }
  const initialized = new WeakSet();
  const headerControllers = new Map();
  const serviceControllers = new Map();
  const setContrast = value => {
    document.documentElement.classList.toggle('brand-high-contrast', value);
    document.querySelectorAll('[data-brand-contrast]').forEach(button => button.setAttribute('aria-pressed', String(value)));
  };
  let highContrast = false;
  try { highContrast = localStorage.getItem('eastern-jewelry-contrast') === 'true'; } catch (_) {}
  setContrast(highContrast);

  function initialize(root = document) {
    const headers = [...root.querySelectorAll('.brand-header')];
    if (root.matches?.('.brand-header')) headers.unshift(root);
    headers.forEach(header => {
      if (initialized.has(header)) return;
      initialized.add(header);
      setContrast(document.documentElement.classList.contains('brand-high-contrast'));
      const listeners = [];
      const listen = (target, type, handler, options) => {
        if (!target) return;
        target.addEventListener(type, handler, options);
        listeners.push(() => target.removeEventListener(type, handler, options));
      };
      const updateHeader = () => {
        header.classList.toggle('is-scrolled', window.scrollY > 0);
        // The 57px search row has fully passed beneath the sticky mobile header.
        header.classList.toggle('is-mobile-search-away', window.scrollY >= 57);
        const headerBounds = header.getBoundingClientRect();
        header.style.setProperty('--brand-header-bottom', `${Math.max(headerBounds.height, headerBounds.bottom)}px`);
      };
      updateHeader();
      const announcement = header.previousElementSibling?.matches('[data-brand-contact-announcement]') ? header.previousElementSibling : header.previousElementSibling?.querySelector('[data-brand-contact-announcement]');
      listen(announcement, 'click', () => { announcement.focus(); header.querySelector('[data-brand-contact]')?.click(); });
      listen(window, 'scroll', updateHeader, { passive: true });
      listen(window, 'resize', updateHeader);
      listen(header.querySelector('[data-brand-contrast]'), 'click', () => {
        highContrast = !document.documentElement.classList.contains('brand-high-contrast');
        setContrast(highContrast);
        try { localStorage.setItem('eastern-jewelry-contrast', String(highContrast)); } catch (_) {}
      });
      const primary = header.querySelector('.brand-menu-primary');
      const menu = header.querySelector('.brand-nav');
      let submenuTrigger = null;
      const submenuMediaUpdates = new Map();
      const resetSubmenus = () => {
        header.querySelectorAll('[data-brand-submenu-panel]').forEach(panel => { panel.hidden = true; });
        header.querySelectorAll('[data-brand-submenu]').forEach(button => button.setAttribute('aria-expanded', 'false'));
        if (primary) primary.hidden = false;
        menu?.classList.remove('has-submenu');
      };
      header.querySelectorAll('[data-brand-submenu]').forEach(button => {
        listen(button, 'click', () => {
          const panel = document.getElementById(button.getAttribute('aria-controls'));
          if (!panel || !primary) return;
          resetSubmenus();
          submenuTrigger = button;
          button.setAttribute('aria-expanded', 'true');
          primary.hidden = true;
          menu?.classList.add('has-submenu');
          panel.hidden = false;
          panel.querySelectorAll('[data-brand-submenu-media]').forEach(track => submenuMediaUpdates.get(track)?.());
          panel.querySelector('[data-brand-submenu-back]')?.focus();
        });
      });
      header.querySelectorAll('[data-brand-submenu-back]').forEach(button => listen(button, 'click', () => {
        resetSubmenus();
        submenuTrigger?.focus();
      }));
      listen(header.querySelector('[data-brand-menu]'), 'click', resetSubmenus);
      menu?.querySelectorAll('[data-brand-menu-close]').forEach(button => listen(button, 'click', resetSubmenus));

      header.querySelectorAll('[data-brand-submenu-media]').forEach(track => {
        const shell = track.closest('.brand-submenu-media-shell');
        const previous = shell?.querySelector('[data-brand-submenu-prev]');
        const next = shell?.querySelector('[data-brand-submenu-next]');
        if (!previous || !next) return;
        const update = () => {
          if (!track.clientWidth) return;
          previous.hidden = track.scrollLeft <= 1;
          next.hidden = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
        };
        const advance = direction => {
          const card = track.querySelector('.brand-submenu-card');
          const step = card ? card.getBoundingClientRect().width + 8 : track.clientWidth;
          track.scrollBy({ left: direction * step, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
        };
        listen(previous, 'click', () => advance(-1));
        listen(next, 'click', () => advance(1));
        listen(track, 'scroll', update, { passive: true });
        listen(window, 'resize', update);
        submenuMediaUpdates.set(track, update);
        update();
      });

      const panel = header.querySelector('[data-brand-search-panel]');
      if (!panel) return;
      const panelId = panel.id;
      const triggers = [...document.querySelectorAll('[data-brand-search]')].filter(button => button.getAttribute('aria-controls') === panelId);
      const closeButton = panel.querySelector('[data-brand-search-close]');
      const backdrop = header.querySelector('[data-brand-search-backdrop]');
      const input = panel.querySelector('[data-brand-search-input]');
      input.maxLength = 256;
      const results = panel.querySelector('[data-brand-search-results]');
      const status = panel.querySelector('[data-brand-search-count]');
      const clearButton = panel.querySelector('[data-brand-search-clear]');
      const trending = panel.querySelector('[data-brand-search-trending]');
      const recovery = panel.querySelector('[data-brand-search-recovery]');
      const emptyMessage = panel.querySelector('[data-brand-search-empty-message]');
      const suggestionButton = document.createElement('button');
      suggestionButton.type = 'button'; suggestionButton.className = 'brand-text-link';
      suggestionButton.hidden = true;
      recovery?.append(suggestionButton);
      const fallbackMarkup = results.innerHTML;
      let open = false;
      let trigger = null;
      let indexPromise = null;
      let searchVersion = 0;
      let searchTimer;
      let priorBodyLocked = false;
      let searchOwnsUrl = false;
      const inertState = new Map();
      const getIndex = () => {
        if (!indexPromise) indexPromise = fetch(panel.dataset.indexUrl, { credentials: 'omit' }).then(response => {
          if (!response.ok) throw new Error('Search index unavailable');
          return response.json().then(readSearchIndex).then(pages => pages.map(page => window.EasternNativeCatalog?.apply(page) || page));
        }).catch(error => { indexPromise = null; throw error; });
        return indexPromise;
      };
      const updateSearchUrl = () => {
        if (!open || !searchOwnsUrl) return;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('search', input.value.trim());
          history.replaceState(history.state, '', url);
        } catch (_) { /* Embedded previews may deny History writes. */ }
      };
      const renderLink = page => {
        const link = document.createElement('a');
        link.href = page.url;
        const title = document.createElement('span'); title.textContent = page.title;
        if (page.piece_id && page.image) {
          link.className = 'brand-search-piece';
          const image = document.createElement('img');
          image.src = new URL(page.image, new URL(panel.dataset.indexUrl, document.baseURI)).href;
          image.alt = page.image_alt || page.title;
          image.width = 640; image.height = 800;
          image.loading = 'lazy'; image.decoding = 'async';
          const info = document.createElement('div'); info.className = 'brand-search-product-info';
          info.append(title); link.append(image, info);
          if (validSellingPrice(page)) {
            const price = document.createElement('p');
            price.className = 'ej-piece-price brand-search-price';
            price.dataset.piecePrice = page.piece_id;
            price.dataset.priceAmount = String(page.price_amount);
            price.dataset.priceCurrency = page.price_currency;
            const label = document.createElement('span');
            label.className = 'ej-piece-price-label'; label.textContent = 'Price';
            const amount = document.createElement('span');
            amount.className = 'ej-piece-price-amount';
            amount.setAttribute('translate', 'no'); amount.setAttribute('data-i18n-ignore', '');
            amount.textContent = formatSellingPrice(page);
            price.append(label, amount);
            info.append(price);
          }
          if (page.native_product && !page.available) {
            const status = document.createElement('p'); status.className = 'ej-native-card-status';
            status.textContent = 'Sold out'; info.append(status);
          }
          const card = document.createElement('article');
          card.className = 'brand-search-piece-card';
          const save = document.createElement('button');
          save.type = 'button'; save.className = 'brand-search-save';
          save.dataset.savePiece = page.piece_id;
          save.dataset.pieceName = page.title;
          save.setAttribute('aria-pressed', 'false');
          save.setAttribute('aria-label', `Save ${page.title} to saved creations`);
          const heart = header.querySelector('.brand-header-saved .brand-icon')?.cloneNode(true);
          if (heart) {
            // The filled state follows the outer contour of the captured heart.
            const fill = document.createElementNS('http://www.w3.org/2000/svg', 'path');
            fill.setAttribute('class', 'brand-search-heart-fill');
            fill.setAttribute('d', 'M11.998 21l-.363-.23C11.242 20.518 2 14.585 2 9.496c0-1.666.506-3.236 1.424-4.422C4.476 3.718 5.948 3 7.681 3c2.17 0 3.564 1.322 4.303 2.312C12.704 4.317 14.091 3 16.385 3 20.032 3 22 6.347 22 9.497c0 5.16-9.244 11.027-9.638 11.274z');
            heart.append(fill); save.append(heart);
          } else save.textContent = 'Save';
          card.append(link, save);
          return card;
        } else {
          link.className = 'brand-search-page';
          const description = document.createElement('small'); description.textContent = page.description;
          link.append(title, description);
        }
        return link;
      };
      const renderResults = async () => {
        const version = ++searchVersion;
        input.value = input.value.slice(0, 256);
        const terms = searchTokens(input.value.trim());
        if (clearButton) clearButton.hidden = !input.value;
        panel.classList.toggle('has-query', !!terms.length);
        updateSearchUrl();
        try {
          const pages = await getIndex();
          if (version !== searchVersion) return;
          const matches = pages.map(page => scoreSearchPage(page, terms))
            .filter(page => !terms.length || page.matches).sort((a, b) => b.score - a.score);
          const matchingProducts = matches.filter(page => page.piece_id && page.image);
          const matchingPages = matches.filter(page => !(page.piece_id && page.image));
          const isEmpty = terms.length > 0 && matches.length === 0;
          const featured = pages.filter(page => page.piece_id && page.image).slice(0, 12);
          const shown = terms.length && !isEmpty ? [...matchingProducts, ...matchingPages] : featured.length ? featured : pages.filter(page => ['/pages/jewelry', '/pages/jewelry-world', '/pages/client-services', '/pages/client-care'].includes(page.url));
          panel.classList.toggle('has-no-results', isEmpty);
          if (trending) trending.hidden = terms.length > 0 && !isEmpty;
          if (recovery) recovery.hidden = !isEmpty;
          if (emptyMessage) emptyMessage.textContent = isEmpty ? `There are no items matching “${input.value.trim()}”.` : '';
          const suggestion = isEmpty ? suggestSearchQuery(terms, pages) : '';
          suggestionButton.hidden = !suggestion;
          suggestionButton.dataset.searchSuggestion = suggestion;
          suggestionButton.textContent = suggestion ? `Try “${suggestion}”` : '';
          results.replaceChildren();
          status.textContent = terms.length && !isEmpty
            ? [matchingProducts.length ? `${matchingProducts.length} ${matchingProducts.length === 1 ? 'creation' : 'creations'}` : '',
              matchingPages.length ? `${matchingPages.length} ${matchingPages.length === 1 ? 'page' : 'pages'}` : ''].filter(Boolean).join(' · ')
            : 'Colorful Creations';
          shown.forEach((page, index) => {
            if (terms.length && !isEmpty && matchingPages.length && index === matchingProducts.length) {
              const heading = document.createElement('h3');
              heading.className = 'brand-search-section-title';
              heading.textContent = matchingProducts.length ? 'Related Pages' : 'Pages & Guides';
              results.append(heading);
            }
            if ((!terms.length || isEmpty) && index === 6 && featured.length) {
              const heading = document.createElement('h3');
              heading.className = 'brand-search-section-title'; heading.textContent = 'More Creations';
              results.append(heading);
            }
            results.append(renderLink(page));
          });
          document.dispatchEvent(new CustomEvent('brand:search-rendered'));
          if (!shown.length) {
            results.innerHTML = fallbackMarkup;
          }
        } catch (_) {
          if (version !== searchVersion) return;
          status.textContent = 'Search is temporarily unavailable. Please explore the links below.';
          results.innerHTML = fallbackMarkup;
          if (trending) trending.hidden = false;
          if (recovery) recovery.hidden = true;
        }
      };
      const close = (restoreFocus = true) => {
        if (!open) return;
        open = false;
        clearTimeout(searchTimer);
        searchVersion += 1;
        if (searchOwnsUrl) {
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete('search');
            history.replaceState(history.state, '', url);
          } catch (_) { /* The dialog still closes if URL writes are denied. */ }
          searchOwnsUrl = false;
        }
        panel.hidden = true;
        if (backdrop) backdrop.hidden = true;
        triggers.forEach(button => button.setAttribute('aria-expanded', 'false'));
        inertState.forEach((value, element) => value === null ? element.removeAttribute('inert') : element.setAttribute('inert', value));
        inertState.clear();
        if (!priorBodyLocked) document.body.classList.remove('brand-menu-open');
        if (restoreFocus) {
          const target = trigger?.isConnected && trigger.getClientRects().length ? trigger : triggers.find(button => button.isConnected && button.getClientRects().length);
          target?.focus();
        }
      };
      const show = button => {
        if (open) return;
        document.dispatchEvent(new CustomEvent('brand:panel-opening', { detail: { panel } }));
        trigger = button;
        priorBodyLocked = document.body.classList.contains('brand-menu-open');
        document.body.classList.add('brand-menu-open');
        [...document.querySelectorAll('main, footer'), ...document.querySelectorAll('.brand-mobile-search')].forEach(element => {
          inertState.set(element, element.getAttribute('inert'));
          element.setAttribute('inert', '');
        });
        open = true;
        searchOwnsUrl = true;
        panel.hidden = false;
        if (backdrop) backdrop.hidden = false;
        triggers.forEach(control => control.setAttribute('aria-expanded', 'true'));
        if (!reducedMotion.matches && typeof panel.animate === 'function') panel.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 300, easing: 'ease-out' });
        input.focus();
        renderResults();
      };
      triggers.forEach(button => listen(button, 'click', () => show(button)));
      listen(closeButton, 'click', () => close());
      listen(backdrop, 'click', () => close());
      listen(panel.querySelector('[data-brand-search-form]'), 'submit', event => { event.preventDefault(); clearTimeout(searchTimer); renderResults(); });
      listen(input, 'input', () => { if (clearButton) clearButton.hidden = !input.value; clearTimeout(searchTimer); searchTimer = setTimeout(renderResults, 120); });
      listen(clearButton, 'click', () => { input.value = ''; clearTimeout(searchTimer); renderResults(); input.focus(); });
      listen(suggestionButton, 'click', () => { input.value = suggestionButton.dataset.searchSuggestion || ''; clearTimeout(searchTimer); renderResults(); input.focus(); });
      panel.querySelectorAll('[data-brand-search-term]').forEach(button => listen(button, 'click', () => {
        input.value = button.dataset.brandSearchTerm;
        clearTimeout(searchTimer); renderResults(); input.focus();
      }));
      listen(input, 'search', renderResults);
      listen(document, 'brand:languagechange', () => { if (open) renderResults(); });
      listen(window, 'popstate', () => {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('search')) close(false);
        else { input.value = url.searchParams.get('search') || ''; if (!open) show(triggers[0]); else renderResults(); }
      });
      listen(document, 'brand:panel-opening', event => { if (event.detail?.panel !== panel) close(false); });
      const handleSearchKeydown = event => {
        if (!open) return;
        if (event.key === 'Escape') { event.preventDefault(); close(); return; }
        if (event.key !== 'Tab') return;
        const controls = [...panel.querySelectorAll('a[href],button,input')].filter(element => !element.disabled && element.getClientRects().length > 0);
        const first = controls[0], last = controls.at(-1);
        if (!panel.contains(document.activeElement) || (event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
          event.preventDefault(); (event.shiftKey ? last : first)?.focus();
        }
      };
      const handleSearchFocus = event => { if (open && !panel.contains(event.target)) input.focus(); };
      listen(document, 'keydown', handleSearchKeydown);
      listen(document, 'focusin', handleSearchFocus);
      const initialSearch = new URL(window.location.href).searchParams;
      if (initialSearch.has('search')) { input.value = initialSearch.get('search') || ''; show(triggers[0]); }
      headerControllers.set(header, () => {
        close(false);
        clearTimeout(searchTimer);
        searchVersion += 1;
        listeners.forEach(remove => remove());
        initialized.delete(header);
        headerControllers.delete(header);
      });
    });
    root.querySelectorAll('[data-brand-service-menu]').forEach(button => {
      if (serviceControllers.has(button)) return;
      const nav = button.closest('.brand-service-nav');
      const links = document.getElementById(button.getAttribute('aria-controls'));
      if (!nav || !links) return;
      const mobile = window.matchMedia('(max-width: 640px)');
      let open = false;
      const update = () => {
        links.hidden = mobile.matches && !open;
        button.setAttribute('aria-expanded', String(mobile.matches && open));
      };
      const close = (restoreFocus = false) => {
        if (!open) return;
        open = false;
        update();
        if (restoreFocus) button.focus();
      };
      const click = () => { if (mobile.matches) { open = !open; update(); } };
      const outside = event => { if (!nav.contains(event.target)) close(); };
      const keydown = event => { if (open && mobile.matches && event.key === 'Escape') { event.preventDefault(); close(true); } };
      const navigation = event => { if (event.target.closest('a[href]')) close(); };
      const panelOpening = () => close();
      button.addEventListener('click', click);
      links.addEventListener('click', navigation);
      document.addEventListener('click', outside);
      document.addEventListener('keydown', keydown);
      document.addEventListener('brand:panel-opening', panelOpening);
      mobile.addEventListener('change', update);
      update();
      serviceControllers.set(button, () => {
        close();
        button.removeEventListener('click', click);
        links.removeEventListener('click', navigation);
        document.removeEventListener('click', outside);
        document.removeEventListener('keydown', keydown);
        document.removeEventListener('brand:panel-opening', panelOpening);
        mobile.removeEventListener('change', update);
        serviceControllers.delete(button);
      });
    });
    root.querySelectorAll('[data-brand-full-name]').forEach(field => {
      if (initialized.has(field)) return;
      initialized.add(field);
      const form = field.closest('form');
      const first = form?.querySelector('[data-brand-first-name]');
      const last = form?.querySelector('[data-brand-last-name]');
      if (!form || !first || !last) return;
      const syncName = () => { field.value = [first.value.trim(), last.value.trim()].filter(Boolean).join(' '); };
      first.addEventListener('input', syncName);
      last.addEventListener('input', syncName);
      form.addEventListener('submit', syncName, { capture: true });
      syncName();
    });
  }
  initialize();
  document.addEventListener('shopify:section:load', event => initialize(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [button, destroy] of serviceControllers) {
      if (event.target === button || event.target.contains(button)) destroy();
    }
    for (const [header, destroy] of headerControllers) {
      if (event.target === header || event.target.contains(header)) destroy();
    }
  });
})();
